import * as React from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SendHorizonal, Sparkles } from "lucide-react";
import { IceBreakers } from "./ice-breakers";
import { useHapticFeedback } from "@/hooks/use-haptic";
import { detectLanguage, getFontFamilyForText } from "@/lib/language-detector";
import { Trans } from "react-i18next";
import { useTranslation } from "@/lib/translation-utils";
import { useLanguage } from "@/lib/language-context";
import i18next from "i18next";

type ChatInputProps = {
  onSend: (message: string) => void;
  disabled?: boolean;
  elapsedTime?: number;
  isPending?: boolean;
};

const MAX_CHARS = 750;

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled,
  elapsedTime = 0,
  isPending = false
}) => {
  const [message, setMessage] = React.useState("");
  // Get the current language context
  const { locale, isRTL: isLanguageRTL } = useLanguage();
  // Get translation function
  const { t } = useTranslation();
  // Direction state for the input (initially based on language, can change with content)
  const [inputDirection, setInputDirection] = React.useState<'rtl' | 'ltr'>(isLanguageRTL ? 'rtl' : 'ltr');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const { triggerHaptic } = useHapticFeedback();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Auto-resize textarea height when content changes
  const autoResizeTextarea = React.useCallback(() => {
    if (textareaRef.current) {
      // Reset height to auto to get correct scrollHeight measurement
      textareaRef.current.style.height = 'auto';
      
      // Set height based on content
      const scrollHeight = textareaRef.current.scrollHeight;
      
      // For empty or initial content, use a consistent initial height of 56px for all languages
      const height = message.trim() ? Math.max(56, scrollHeight) : 56;
      textareaRef.current.style.height = `${height}px`;
      
      // For browsers that support it, also set a data attribute that our CSS can use
      // Find the nearest matching data-height value from our predefined heights
      if (height <= 24) textareaRef.current.setAttribute('data-height', '24');
      else if (height <= 48) textareaRef.current.setAttribute('data-height', '48');
      else if (height <= 64) textareaRef.current.setAttribute('data-height', '64');
      else if (height <= 72) textareaRef.current.setAttribute('data-height', '72');
      else if (height <= 96) textareaRef.current.setAttribute('data-height', '96');
      else if (height <= 120) textareaRef.current.setAttribute('data-height', '120');
      else if (height <= 144) textareaRef.current.setAttribute('data-height', '144');
      else if (height <= 168) textareaRef.current.setAttribute('data-height', '168');
      else textareaRef.current.setAttribute('data-height', '192');
    }
  }, [message]);
  
  // Initialize auto-resize
  React.useEffect(() => {
    if (textareaRef.current) {
      // Add the textarea-auto-height class for styling
      textareaRef.current.classList.add('textarea-auto-height');
      
      // Initial resize
      autoResizeTextarea();
    }
  }, [autoResizeTextarea]);
  
  // Handle resize on content change
  React.useEffect(() => {
    autoResizeTextarea();
  }, [message, autoResizeTextarea]);

  // Set default direction when language changes
  React.useEffect(() => {
    setInputDirection(isLanguageRTL ? 'rtl' : 'ltr');
  }, [isLanguageRTL]);

  // Detect text direction from content and update direction
  React.useEffect(() => {
    if (message.trim()) {
      const lang = detectLanguage(message);
      setInputDirection(lang === 'ar' ? 'rtl' : 'ltr');
    } else {
      // When input is empty, use the language's default direction
      setInputDirection(isLanguageRTL ? 'rtl' : 'ltr');
    }
  }, [message, isLanguageRTL]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await triggerHaptic('selection');
      } catch (error) {
        console.warn('[ChatInput] Haptic feedback failed:', error);
      } finally {
        onSend(message.trim());
        setMessage("");
        textareaRef.current?.blur();
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const remainingChars = MAX_CHARS - message.length;
  
  // Get detected language and check if it's RTL
  const detectedLang = detectLanguage(message);
  
  // Use proper i18n for character count message with pluralization
  const charactersText = remainingChars === 1 ? (
    <Trans>{{remainingChars}} character left</Trans>
  ) : (
    <Trans>{{remainingChars}} characters left</Trans>
  );

  // Direction and language detection are now handled via data attributes and CSS classes

  const inputClassNames = [
    'min-h-[56px]',
    'overflow-y-hidden',
    'pl-12 pr-16 pb-8',
    'text-sm sm:text-base',
    'placeholder:text-sm',
    'chat-input-textarea',
    inputDirection === 'rtl' ? 'text-arabic' : '',
    inputDirection === 'rtl' ? 'font-family-arabic' : 'font-family-english',
  ].filter(Boolean).join(' ');

  const characterCountClassNames = [
    'absolute',
    'bottom-1.5 left-12 right-16',
    'text-xs sm:text-sm',
    'text-muted-foreground',
    'font-mono',
    'z-10', // Ensure it's always on top
    'bg-background/80 backdrop-blur-[2px] rounded-b-lg', // Semi-transparent background without padding
    inputDirection === 'rtl' ? 'font-family-arabic' : 'font-family-english',
  ].filter(Boolean).join(' ');

  // Create data attributes for RTL and language
  const directionAttrs = {
    'data-direction': inputDirection,
    'data-language': detectedLang,
    'data-rtl': inputDirection === 'rtl' ? 'true' : 'false',
  };

  return (
    <form onSubmit={handleSubmit} className="relative chat-input-form">
      <div className="relative flex items-center">
        <div className="absolute left-2 z-10">
          <IceBreakers
            onSelect={async (selectedMessage) => {
              setMessage(selectedMessage);
              if (textareaRef.current) {
                textareaRef.current.focus();
                // Immediately resize after selecting a suggestion
                setTimeout(() => {
                  if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                    const scrollHeight = textareaRef.current.scrollHeight;
                    const height = selectedMessage.trim() ? Math.max(56, scrollHeight) : 56;
                    textareaRef.current.style.height = `${height}px`;
                    
                    // Set data attribute for height
                    if (height <= 24) textareaRef.current.setAttribute('data-height', '24');
                    else if (height <= 48) textareaRef.current.setAttribute('data-height', '48');
                    else if (height <= 64) textareaRef.current.setAttribute('data-height', '64');
                    else if (height <= 72) textareaRef.current.setAttribute('data-height', '72');
                    else if (height <= 96) textareaRef.current.setAttribute('data-height', '96');
                    else if (height <= 120) textareaRef.current.setAttribute('data-height', '120');
                    else if (height <= 144) textareaRef.current.setAttribute('data-height', '144');
                    else if (height <= 168) textareaRef.current.setAttribute('data-height', '168');
                    else textareaRef.current.setAttribute('data-height', '192');
                  }
                }, 0);
              }
            }}
            trigger={
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                aria-label={t("Show message suggestions")}
              >
                <Sparkles className="h-4 w-4" />
              </Button>
            }
          />
        </div>
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            // Immediate resize on input to prevent any lag
            if (textareaRef.current) {
              textareaRef.current.style.height = 'auto';
              const scrollHeight = textareaRef.current.scrollHeight;
              const height = e.target.value.trim() ? Math.max(56, scrollHeight) : 56;
              textareaRef.current.style.height = `${height}px`;
              
              // Set data attribute for height
              if (height <= 24) textareaRef.current.setAttribute('data-height', '24');
              else if (height <= 48) textareaRef.current.setAttribute('data-height', '48');
              else if (height <= 64) textareaRef.current.setAttribute('data-height', '64');
              else if (height <= 72) textareaRef.current.setAttribute('data-height', '72');
              else if (height <= 96) textareaRef.current.setAttribute('data-height', '96');
              else if (height <= 120) textareaRef.current.setAttribute('data-height', '120');
              else if (height <= 144) textareaRef.current.setAttribute('data-height', '144');
              else if (height <= 168) textareaRef.current.setAttribute('data-height', '168');
              else textareaRef.current.setAttribute('data-height', '192');
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={t("Type your message...")}
          id="chat-message-input"
          name="chat-message"
          className={inputClassNames}
          disabled={disabled}
          rows={1}
          maxLength={MAX_CHARS}
          {...directionAttrs}
          aria-label="Chat message input"
        />
        {message.length > 0 && (
          <div
            className={characterCountClassNames}
            dir={inputDirection}
            {...directionAttrs}
            aria-live="polite"
          >
            {charactersText}
          </div>
        )}
        <div className="absolute right-2 inset-y-0 flex items-center">
          <Button
            type="submit"
            size="icon"
            disabled={!message.trim() || disabled}
            aria-label={isPending 
            ? t("Processing message ({seconds} seconds)", { seconds: elapsedTime }) 
            : t("Send message")}
          >
            {isPending ? (
              <div className="flex items-center justify-center w-full min-w-[40px]" aria-hidden="true">
                <span className="text-xs sm:text-sm font-mono">{elapsedTime}s</span>
              </div>
            ) : (
              <SendHorizonal className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};